/** 供应商报价时效沿用项目单位编码：1=小时，2=天。 */
export const QUOTATION_TIME_UNIT = { HOUR: 1, DAY: 2 } as const
const HOURS_PER_DAY = 24

export type QuotationTimeField = 'pickupLeadTime' | 'portToPortDays' | 'clearanceDeliveryDays'
export type QuotationTimeUnitField = `${QuotationTimeField}Unit`
export type QuotationTimeliness = Partial<Record<QuotationTimeField | QuotationTimeUnitField, number | null>> & {
  prePortEnable?: unknown
  lastMileEnable?: unknown
}

const isEnabled = (value: unknown) => value === true || value === 1 || value === '1' || value === 'true'

/** 只计算服务范围内的分段，独立港前/尾程不包含干线。 */
export function getQuotationTimeFields(type: string, quotation: QuotationTimeliness): QuotationTimeField[] {
  if (type === 'prePort') return ['pickupLeadTime']
  if (type === 'lastMile') return ['clearanceDeliveryDays']
  return [
    ...(isEnabled(quotation.prePortEnable) ? ['pickupLeadTime' as const] : []),
    'portToPortDays',
    ...(isEnabled(quotation.lastMileEnable) ? ['clearanceDeliveryDays' as const] : [])
  ]
}

/** 历史已填写数值按原有天解释；新空运干线默认小时，其他分段默认天。 */
export function getQuotationTimeUnit(type: string, quotation: QuotationTimeliness, field: QuotationTimeField): number {
  const unit = Number(quotation[`${field}Unit`])
  if (unit === QUOTATION_TIME_UNIT.HOUR || unit === QUOTATION_TIME_UNIT.DAY) return unit
  return type === 'air' && field === 'portToPortDays' && (quotation[field] === null || quotation[field] === undefined)
    ? QUOTATION_TIME_UNIT.HOUR
    : QUOTATION_TIME_UNIT.DAY
}

/** 全部分段填写后统一换算小时；缺值不输出部分和，切换单位不修改原输入值。 */
export function calculateQuotationTimeHours(type: string, quotation: QuotationTimeliness): number | null {
  const fields = getQuotationTimeFields(type, quotation)
  let total = 0
  for (const field of fields) {
    const value = quotation[field]
    if (value === null || value === undefined || !Number.isFinite(Number(value)) || Number(value) < 0) return null
    total += Number(value) * (getQuotationTimeUnit(type, quotation, field) === QUOTATION_TIME_UNIT.DAY ? HOURS_PER_DAY : 1)
  }
  return total
}

/** 将计算结果拆为天和剩余小时，零时长保留为0小时。 */
export function splitQuotationTimeHours(hours: number): { days: number; hours: number } {
  return { days: Math.floor(hours / HOURS_PER_DAY), hours: hours % HOURS_PER_DAY }
}

/** 查询展示保留分段原单位，总时效按天和小时拆分；旧记录空单位按天解释。 */
export function formatQuotationTimeValue(value: unknown, unit: unknown, dayLabel: string, hourLabel: string, combined = false): string {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '-'
  const isHour = Number(unit) === QUOTATION_TIME_UNIT.HOUR
  const separator = /^[a-z]/i.test(dayLabel) ? ' ' : ''
  if (!combined) return `${value}${separator}${isHour ? hourLabel : dayLabel}`
  const duration = splitQuotationTimeHours(Number(value) * (isHour ? 1 : HOURS_PER_DAY))
  return [duration.days ? `${duration.days}${separator}${dayLabel}` : '',
    duration.hours || !duration.days ? `${duration.hours}${separator}${hourLabel}` : ''].filter(Boolean).join(separator)
}
